import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Share2, UserPlus } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { PlanExplanation } from '../components/PlanExplanation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, planId } = location.state || {};
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  if (!plan) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-[#5C5C66] mb-4">No plan data found.</p>
          <button
            onClick={() => navigate('/app')}
            className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-6 py-3 font-semibold"
          >
            Create a Plan
          </button>
        </div>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'My Financial Action Plan - Clarity',
        text: 'Check out my personalized financial plan from Clarity!',
        url: window.location.href
      }).catch(err => console.log('Share failed:', err));
    } else {
      alert('Sharing not supported on this device');
    }
  };

  const handleFeedback = async (adviceUseful, wouldPay) => {
    try {
      await axios.post(`${API}/feedback`, {
        plan_id: planId,
        advice_useful: adviceUseful,
        would_pay: wouldPay
      });
      setFeedbackSubmitted(true);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E5E5EB] py-4">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <h1 className="text-2xl font-black tracking-tighter cursor-pointer" onClick={() => navigate('/')} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Clarity
          </h1>
        </div>
      </nav>

      {/* Results Section */}
      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0B] mb-12 text-center">
              Your Personalised Action Plan
            </h2>

            <div className="grid lg:grid-cols-3 gap-6 mb-12">
              {/* Step 1 */}
              <motion.div
                data-testid="result-step-1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="bg-white border border-[#E5E5EB] rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-[#534AB7] text-white rounded-xl mb-4 font-bold text-xl">
                  1
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-[#0A0A0B] mb-2">{plan.step1_title}</h3>
                <p className="text-2xl font-bold text-[#534AB7] mb-4">{plan.step1_amount}</p>
                <PlanExplanation text={plan.step1_explanation} className="text-base text-[#5C5C66] leading-relaxed" />
              </motion.div>

              {/* Step 2 */}
              <motion.div
                data-testid="result-step-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="bg-white border border-[#E5E5EB] rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-[#534AB7] text-white rounded-xl mb-4 font-bold text-xl">
                  2
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-[#0A0A0B] mb-2">{plan.step2_title}</h3>
                <p className="text-2xl font-bold text-[#534AB7] mb-4">{plan.step2_amount}</p>
                <PlanExplanation text={plan.step2_explanation} className="text-base text-[#5C5C66] leading-relaxed" />
              </motion.div>

              {/* Step 3 */}
              <motion.div
                data-testid="result-step-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="bg-white border border-[#E5E5EB] rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-[#534AB7] text-white rounded-xl mb-4 font-bold text-xl">
                  3
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-[#0A0A0B] mb-2">{plan.step3_title}</h3>
                <p className="text-2xl font-bold text-[#534AB7] mb-4">{plan.step3_amount}</p>
                <PlanExplanation text={plan.step3_explanation} className="text-base text-[#5C5C66] leading-relaxed" />
              </motion.div>
            </div>

            {/* Save to account CTA */}
            {!user && (
              <div data-testid="save-plan-cta" className="bg-[#534AB7]/5 border border-[#534AB7]/20 rounded-2xl p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <h3 className="text-lg font-semibold text-[#0A0A0B] mb-1">Don't lose this plan</h3>
                  <p className="text-sm text-[#5C5C66]">Create a free account to save it, track your progress, and get your Financial Health Score.</p>
                </div>
                <button
                  data-testid="save-plan-btn"
                  onClick={() => navigate('/auth', { state: { planId } })}
                  className="bg-[#534AB7] text-white hover:bg-[#423B99] rounded-xl px-6 py-3.5 font-semibold inline-flex items-center gap-2 whitespace-nowrap"
                >
                  <UserPlus size={18} />
                  Save My Plan Free
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-12">
              <button
                data-testid="share-plan-btn"
                onClick={handleShare}
                className="bg-white text-[#0A0A0B] border border-[#E5E5EB] hover:border-[#534AB7] transition-colors duration-200 rounded-xl px-8 py-4 font-semibold text-lg inline-flex items-center justify-center gap-2"
              >
                <Share2 size={20} />
                Share this plan
              </button>

              <button
                data-testid="upsell-premium-btn"
                onClick={() => user ? navigate('/dashboard?premium=1') : navigate('/auth', { state: { planId, intent: 'premium' } })}
                className="bg-[#534AB7] text-white hover:bg-[#423B99] hover:-translate-y-0.5 transition-transform duration-200 rounded-xl px-8 py-4 font-semibold text-lg inline-flex items-center justify-center"
              >
                Get Premium — from RM10/month
              </button>
            </div>

            {/* Feedback Form */}
            {!feedbackSubmitted ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-[#F6F6F9] border border-[#E5E5EB] rounded-2xl p-8"
              >
                <h3 className="text-xl sm:text-2xl font-semibold text-[#0A0A0B] mb-6">Quick Feedback</h3>
                
                <div className="space-y-6">
                  <div>
                    <p className="text-base text-[#5C5C66] mb-3">Was this advice useful?</p>
                    <div className="flex gap-4">
                      <button
                        data-testid="feedback-useful-yes-btn"
                        onClick={() => handleFeedback(true, false)}
                        className="flex-1 bg-white text-[#0A0A0B] border border-[#E5E5EB] hover:border-[#534AB7] transition-colors rounded-xl px-6 py-3 font-medium"
                      >
                        Yes
                      </button>
                      <button
                        data-testid="feedback-useful-no-btn"
                        onClick={() => handleFeedback(false, false)}
                        className="flex-1 bg-white text-[#0A0A0B] border border-[#E5E5EB] hover:border-[#534AB7] transition-colors rounded-xl px-6 py-3 font-medium"
                      >
                        No
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-base text-[#5C5C66] mb-3">Would you pay RM15/month for a full financial plan?</p>
                    <div className="flex gap-4">
                      <button
                        data-testid="feedback-pay-yes-btn"
                        onClick={() => handleFeedback(true, true)}
                        className="flex-1 bg-white text-[#0A0A0B] border border-[#E5E5EB] hover:border-[#534AB7] transition-colors rounded-xl px-6 py-3 font-medium"
                      >
                        Yes
                      </button>
                      <button
                        data-testid="feedback-pay-no-btn"
                        onClick={() => handleFeedback(true, false)}
                        className="flex-1 bg-white text-[#0A0A0B] border border-[#E5E5EB] hover:border-[#534AB7] transition-colors rounded-xl px-6 py-3 font-medium"
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="bg-[#F6F6F9] border border-[#534AB7]/20 rounded-2xl p-8 text-center">
                <div className="flex items-center justify-center w-16 h-16 bg-[#10B981] text-white rounded-full mx-auto mb-4">
                  <Check size={32} />
                </div>
                <p className="text-lg text-[#0A0A0B] font-semibold">Thank you for your feedback!</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResultsPage;